import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCalendar, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const MONTHS = [
  "janar",
  "shkurt",
  "mars",
  "prill",
  "maj",
  "qershor",
  "korrik",
  "gusht",
  "shtator",
  "tetor",
  "nëntor",
  "dhjetor",
];
const WEEKDAYS = ["Hë", "Ma", "Më", "En", "Pr", "Sh", "Di"];

function parseIso(value) {
  const match = String(value || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return { year, month, day };
}

function toIso(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatDayMonthYear(parts) {
  if (!parts) return "";
  return `${String(parts.day).padStart(2, "0")}.${String(parts.month).padStart(2, "0")}.${parts.year}`;
}

function maskDayMonthYear(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function parseDisplay(text) {
  const match = String(text || "").match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;
  return parseIso(toIso(Number(match[3]), Number(match[2]), Number(match[1])));
}

export default function DateOfBirthField({ id, name, value, onChange, required }) {
  const selected = parseIso(value);
  const today = new Date();
  const maxYear = today.getFullYear();
  const minYear = maxYear - 120;
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => formatDayMonthYear(selected));
  const [view, setView] = useState(() => ({
    year: selected?.year ?? maxYear - 30,
    month: selected?.month ?? today.getMonth() + 1,
  }));
  const rootRef = useRef(null);
  const todayIso = toIso(maxYear, today.getMonth() + 1, today.getDate());

  useEffect(() => {
    if (focused) return;
    setText(formatDayMonthYear(parseIso(value)));
  }, [value, focused]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const years = useMemo(() => {
    const list = [];
    for (let year = maxYear; year >= minYear; year -= 1) list.push(year);
    return list;
  }, [maxYear, minYear]);

  const cells = useMemo(() => {
    const first = new Date(view.year, view.month - 1, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(view.year, view.month, 0).getDate();
    const items = [];
    for (let index = 0; index < startOffset; index += 1) items.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) items.push(day);
    return items;
  }, [view]);

  const shiftMonth = (delta) => {
    const date = new Date(view.year, view.month - 1 + delta, 1);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    if (year > maxYear || (year === maxYear && month > today.getMonth() + 1)) return;
    if (year < minYear) return;
    setView({ year, month });
  };

  const commitIso = (iso) => {
    onChange({ target: { name, value: iso } });
  };

  const commitText = (raw) => {
    const next = maskDayMonthYear(raw);
    setText(next);
    if (!next) {
      commitIso("");
      return;
    }
    const parts = parseDisplay(next);
    if (!parts) return;
    const iso = toIso(parts.year, parts.month, parts.day);
    if (parts.year < minYear || parts.year > maxYear || iso > todayIso) return;
    commitIso(iso);
  };

  const chooseDay = (day) => {
    const iso = toIso(view.year, view.month, day);
    if (iso > todayIso) return;
    setText(formatDayMonthYear(parseIso(iso)));
    commitIso(iso);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        type="text"
        id={id}
        name={name}
        inputMode="numeric"
        autoComplete="bday"
        placeholder="dd.mm.vvvv"
        value={text}
        onChange={(event) => commitText(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          const parts = parseDisplay(text);
          const iso = parts ? toIso(parts.year, parts.month, parts.day) : "";
          const accepted = parts && parts.year >= minYear && parts.year <= maxYear && iso <= todayIso;
          setText(accepted ? formatDayMonthYear(parts) : formatDayMonthYear(parseIso(value)));
        }}
        className="input h-12 w-full pr-12 text-base"
      />
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Zgjidh datën"
        onClick={() => {
          if (selected) setView({ year: selected.year, month: selected.month });
          setOpen((current) => !current);
        }}
        className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
      >
        <FiCalendar size={16} />
      </button>
      {required ? (
        <input
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute h-0 w-0 opacity-0"
          value={selected ? toIso(selected.year, selected.month, selected.day) : ""}
          required
          onChange={() => {}}
        />
      ) : null}
      {open ? (
        <div
          role="dialog"
          aria-label="Data e lindjes"
          className="absolute z-30 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white p-4 shadow-lg"
        >
          <div className="mb-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
              aria-label="Muaji i mëparshëm"
            >
              <FiChevronLeft size={18} />
            </button>
            <div className="flex min-w-0 flex-1 gap-2">
              <span className="flex h-9 flex-1 items-center justify-center rounded-md bg-slate-50 text-sm font-semibold capitalize text-slate-900">
                {MONTHS[view.month - 1]}
              </span>
              <select
                aria-label="Viti"
                value={view.year}
                onChange={(event) => setView((current) => ({ ...current, year: Number(event.target.value) }))}
                className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-900"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
              aria-label="Muaji i ardhshëm"
            >
              <FiChevronRight size={18} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-slate-400">
            {WEEKDAYS.map((label) => (
              <span key={label} className="py-1">
                {label}
              </span>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {cells.map((day, index) => {
              if (!day) return <span key={`empty-${index}`} />;
              const iso = toIso(view.year, view.month, day);
              const future = iso > todayIso;
              const isSelected = selected && toIso(selected.year, selected.month, selected.day) === iso;
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={future}
                  onClick={() => chooseDay(day)}
                  className={`h-10 rounded-md text-sm font-medium ${
                    isSelected
                      ? "bg-slate-900 text-white"
                      : future
                        ? "text-slate-300"
                        : "text-slate-800 hover:bg-slate-100"
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
